import { Mail, Phone, MapPin, MessageCircle, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useApp } from "@/context/AppContext";

export const Contact = () => {
  const { content, t, wa, lang } = useApp();
  const c = content.contact;

  const mailSubject = lang === "id" ? "Permintaan Kerja Sama / Pemesanan — Yutaka Agro" : "Partnership / Order Inquiry — Yutaka Agro";
  const mailBody = lang === "id"
    ? "Halo Yutaka Agro,\n\nSaya ingin bertanya mengenai kemitraan / pemesanan komoditas.\n\nNama:\nPerusahaan:\nKebutuhan:\n\nTerima kasih."
    : "Hello Yutaka Agro,\n\nI would like to inquire about a partnership / commodity order.\n\nName:\nCompany:\nRequirement:\n\nThank you.";
  const mailto = `mailto:${c.email}?subject=${encodeURIComponent(mailSubject)}&body=${encodeURIComponent(mailBody)}`;

  const rows = [
    { icon: Mail, label: "Email", value: c.email, href: mailto, hint: lang === "id" ? "Kirim email" : "Send email", testid: "contact-email" },
    { icon: Phone, label: lang === "id" ? "Telepon" : "Phone", value: c.phone, href: `tel:${c.phone.replace(/[^+\d]/g, "")}`, hint: lang === "id" ? "Telepon sekarang" : "Call now", testid: "contact-phone" },
    { icon: MapPin, label: lang === "id" ? "Lokasi" : "Location", value: t(c.address), href: null, testid: "contact-address" },
  ];

  const cardClass =
    "group flex items-center gap-5 rounded-2xl border border-[#1E3A2B]/10 bg-[#F2ECE1] px-6 py-5 transition-colors duration-300";

  return (
    <>
      <section id="kontak" data-testid="contact-section" className="px-4 sm:px-8 lg:px-16 py-20 sm:py-28 bg-[#FAF7F2]">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <Reveal>
              <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#D96B27] font-semibold">
                05 // {lang === "id" ? "Kontak" : "Contact"}
              </p>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-4 font-serif font-bold text-3xl sm:text-4xl lg:text-5xl text-[#1E3A2B] tracking-tight leading-[1.12]" data-testid="contact-heading">
                {t(c.heading)}
              </h2>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-5 text-base sm:text-lg text-[#5C6B61] leading-relaxed max-w-lg">{t(c.body)}</p>
            </Reveal>
            <Reveal delay={0.22}>
              <a
                data-testid="whatsapp-cta-button"
                href={wa(lang === "id" ? "Halo Yutaka Agro, saya ingin memulai kerja sama / pemesanan." : "Hello Yutaka Agro, I would like to start a partnership / place an order.")}
                target="_blank"
                rel="noopener noreferrer"
                className="group mt-9 inline-flex items-center gap-3 px-8 py-4 rounded-full bg-[#25D366] text-white text-sm font-bold tracking-wide hover:bg-[#1E3A2B] transition-colors duration-300"
              >
                <MessageCircle size={17} />
                WhatsApp
                <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </a>
            </Reveal>
          </div>

          <div className="flex flex-col justify-center gap-4">
            {rows.map((r, i) => (
              <Reveal key={r.testid} delay={0.1 + i * 0.08}>
                {r.href ? (
                  <a
                    href={r.href}
                    className={`${cardClass} hover:border-[#D96B27]/50 hover:bg-[#EFE6D6] cursor-pointer`}
                    data-testid={r.testid}
                    aria-label={`${r.hint}: ${r.value}`}
                  >
                    <span className="w-11 h-11 shrink-0 rounded-full bg-[#1E3A2B] flex items-center justify-center transition-colors duration-300 group-hover:bg-[#D96B27]">
                      <r.icon size={17} className="text-[#E5B869] transition-colors duration-300 group-hover:text-[#FAF7F2]" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#D96B27] font-semibold">{r.label}</p>
                      <p className="mt-1 font-serif text-lg sm:text-xl font-semibold text-[#1E3A2B] transition-colors duration-300 group-hover:text-[#D96B27] truncate">
                        {r.value}
                      </p>
                    </div>
                    <span className="hidden sm:flex shrink-0 items-center gap-1.5 text-[10px] font-mono uppercase tracking-[0.18em] text-[#1E3A2B]/45 transition-colors duration-300 group-hover:text-[#D96B27]" data-testid={`${r.testid}-hint`}>
                      {r.hint}
                      <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </span>
                    <ArrowUpRight size={15} className="sm:hidden shrink-0 text-[#1E3A2B]/45 group-hover:text-[#D96B27]" />
                  </a>
                ) : (
                  <div className={cardClass} data-testid={r.testid}>
                    <span className="w-11 h-11 shrink-0 rounded-full bg-[#1E3A2B] flex items-center justify-center">
                      <r.icon size={17} className="text-[#E5B869]" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#D96B27] font-semibold">{r.label}</p>
                      <p className="mt-1 font-serif text-lg sm:text-xl font-semibold text-[#1E3A2B]">{r.value}</p>
                    </div>
                  </div>
                )}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-[#14281E] px-4 sm:px-8 lg:px-16 py-10" data-testid="site-footer">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-center gap-4">
            <img src="/logo-mark.png" alt="Yutaka Agro" className="w-16 h-auto shrink-0" data-testid="footer-logo" />
            <p className="font-serif italic text-lg text-[#E5B869]" data-testid="footer-tagline">{t(content.footer.tagline)}</p>
          </div>
          <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-[#FAF7F2]/45">
            © 2026 Yutaka Agro — PT Relasi Andalan Colaborindo · Yutaka Investama Indonesia
          </p>
        </div>
      </footer>
    </>
  );
};
